import { Box, CardContent, Typography } from '@mui/material';
import React, { useEffect } from 'react';
import { alpha } from '@mui/system';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { listOrganisationDailyUpdates } from '../../../../redux/slices/daily-updates';
import { RoleName } from '../../../../types/user';
import {
	ModernCard,
	CardHeader,
	ProgressContainer,
	ModernProgress,
	COLORS,
	STATUS_COLORS
} from '../../../../pages/Organisation/DailyUpdates/utils';
import AdmissionsOfficerSkeleton from '../../../../components/ui/atoms/DailyUpdates/AdmissionsOfficerSkeleton';

const CustomStatusBadge = ({ color, label }: { color: string; label: number | string }) => (
	<Box
		sx={{
			borderRadius: '6px',
			backgroundColor: alpha(color, 0.15),
			color: color,
			fontWeight: 600,
			fontSize: '0.75rem',
			height: '24px',
			padding: '0 8px',
			display: 'inline-flex',
			alignItems: 'center',
			justifyContent: 'center'
		}}
	>
		{label}
	</Box>
);

const StudentDU = () => {
	const dispatch = useAppDispatch();
	const [studentDU, user] = useAppSelector((state) => [
		state.dailyUpdates.dailyUpdates?.[RoleName.Student],
		state.user.currentUser
	]);

	useEffect(() => {
		user?.id &&
			dispatch(
				listOrganisationDailyUpdates({
					roleName: RoleName.Student,
					organisationId: user.id,
					status: null,
					dailyUpdates: true,
					lastLoggedOut: localStorage.getItem(`lastLogout_${user.id}`) || undefined
				})
			);
	}, [dispatch, user?.id]);

	// Calculate total counts for both categories
	const verificationTotalCount =
		(studentDU?.verificationRequests?.pending ?? 0) +
		(studentDU?.verificationRequests?.approved ?? 0) +
		(studentDU?.verificationRequests?.declined ?? 0);

	const documentsTotalCount =
		(studentDU?.sharedDocuments?.completed ?? 0) +
		(studentDU?.sharedDocuments?.pending ?? 0) +
		(studentDU?.sharedDocuments?.dismissed ?? 0);

	// Define status key mappings for display
	const verificationKeyMapping = {
		pending: 'Pending',
		approved: 'Approved',
		declined: 'Declined'
	};

	const documentKeyMapping = {
		completed: 'Completed',
		pending: 'Pending',
		dismissed: 'Dismissed'
	};

	// Define consistent status color mapping
	const statusColorMapping = {
		pending: STATUS_COLORS.pending,
		approved: STATUS_COLORS.completed,
		declined: STATUS_COLORS.dismissed,
		completed: STATUS_COLORS.completed,
		dismissed: STATUS_COLORS.dismissed
	};

	if (!studentDU) {
		return <AdmissionsOfficerSkeleton />;
	}

	return (
		<Box
			sx={{
				p: 3,
				maxWidth: '1400px',
				margin: '0 auto',
				backgroundColor: alpha(COLORS.neutral, 0.5),
				borderRadius: '24px'
			}}
		>
			{/* Stats Overview Cards */}
			<Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
				{/* Verification Requests Card */}
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							Verification Requests
						</Typography>
						<Typography variant="body2" sx={{ color: COLORS.textLight }}>
							{verificationTotalCount} total requests
						</Typography>
					</CardHeader>
					<CardContent sx={{ p: 3 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{(['pending', 'approved', 'declined'] as Array<keyof typeof verificationKeyMapping>).map(
								(key) => {
									const count = studentDU.verificationRequests[key] ?? 0;
									const label = verificationKeyMapping[key];
									const statusColor = statusColorMapping[key];

									return (
										<Box key={key}>
											<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
												<Typography variant="body2" sx={{ color: COLORS.textLight }}>
													{label}
												</Typography>
												<CustomStatusBadge color={statusColor} label={count} />
											</Box>
											<ProgressContainer>
												<ModernProgress
													variant="determinate"
													value={
														verificationTotalCount > 0
															? (count / verificationTotalCount) * 100
															: 0
													}
													barColor={statusColor}
												/>
											</ProgressContainer>
										</Box>
									);
								}
							)}
						</Box>
					</CardContent>
				</ModernCard>

				{/* Shared Documents Card */}
				<ModernCard sx={{ flex: 1 }}>
					<CardHeader>
						<Typography variant="h6" sx={{ fontWeight: 600, color: COLORS.text }}>
							Shared Documents
						</Typography>
						<Typography variant="body2" sx={{ color: COLORS.textLight }}>
							{documentsTotalCount} total documents
						</Typography>
					</CardHeader>
					<CardContent sx={{ p: 3 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{(['completed', 'pending', 'dismissed'] as Array<keyof typeof documentKeyMapping>).map(
								(key) => {
									const count = studentDU.sharedDocuments[key] ?? 0;
									const label = documentKeyMapping[key];
									const statusColor = statusColorMapping[key];

									return (
										<Box key={key}>
											<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
												<Typography variant="body2" sx={{ color: COLORS.textLight }}>
													{label}
												</Typography>
												<CustomStatusBadge color={statusColor} label={count} />
											</Box>
											<ProgressContainer>
												<ModernProgress
													variant="determinate"
													value={
														documentsTotalCount > 0
															? (count / documentsTotalCount) * 100
															: 0
													}
													barColor={statusColor}
												/>
											</ProgressContainer>
										</Box>
									);
								}
							)}
						</Box>
					</CardContent>
				</ModernCard>
			</Box>
		</Box>
	);
};

export default StudentDU;
